import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { authService, User as ApiUser } from '../lib/api/auth.service';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  theme?: 'light' | 'dark';
  language?: 'en' | 'es' | 'sw';
  subscription?: {
    tier: string;
    status: string;
    currentPeriodEnd: string;
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshUser: () => Promise<void>;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  confirmPassword?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if we have stored auth data
        const currentUser = authService.getCurrentUser();
        const token = authService.getToken();
        
        if (token && currentUser) {
          // Validate token by fetching profile from backend
          try {
            const validatedUser = await authService.getProfile();
            setUser(validatedUser);
          } catch (profileError) {
            // Token invalid or expired, try to refresh
            try {
              await authService.refreshToken();
              const refreshedUser = await authService.getProfile();
              setUser(refreshedUser);
            } catch (refreshError) {
              // Refresh failed, clear auth state
              console.error('Session expired:', refreshError);
              await authService.logout();
              setUser(null);
            }
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const login = async (email: string, password: string) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authService.login({ email, password });
      
      if (response.success && response.data.user) {
        setUser(response.data.user);
      } else {
        throw new Error('Login failed');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      console.error('Login failed:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterData) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authService.register({
        email: userData.email,
        password: userData.password,
        name: userData.name,
        confirmPassword: userData.confirmPassword,
      });
      
      if (response.success) {
        // Registration successful - user may need to verify email
        // Don't auto-login if email verification is required
        if (response.data?.user) {
          setUser(response.data.user);
        }
      } else {
        throw new Error('Registration failed');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      console.error('Registration failed:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setError(null);
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const updatedUser = await authService.getProfile();
      setUser(updatedUser);
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    register,
    logout,
    clearError,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
